import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MuiDialog from 'src/components/ui-component/mui-dialog/MuiDialog';
import MUIToolTip from 'src/components/ui-component/mui-tooltip/MUIToolTip';
import { useApplication } from 'src/context/ApplicationContext';
import DeleteRecord from 'src/lib/api/DeleteRecord';
import { APIS } from 'src/lib/constants/apis';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, FORM_FIELDS_TYPES, KOSTEN_ARTEN, KOSTEN_POSITION, KOSTENTRAEGER } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, NonRequired, PostalCodeValidation, Required } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung, generateUniqueId, getFieldNamefromDataPath } from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import FoerderProgrammSchema from 'src/lib/paths/foerderprogramm_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;
const FP_P = FoerderProgrammSchema.attributes;

const StandorteEmobLis: FC<{ groupId: string; hasComment?: boolean }> = ({ groupId, hasComment }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const [standortGroupIdToDelete, setStandortGroupIdToDelete] = useState('');

	const handleAddStandorte = () => {
		let appCopy = cloneDeep(application);

		const uniqueId = generateUniqueId();
		const lastIndexInvestKosten = get(application, P.Foerderantrag_Kostenpositions.path, []).length;

		let standorteData = {
			[getFieldNamefromDataPath(
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes.Group_45_ID.path,
			)]: uniqueId,
			[getFieldNamefromDataPath(
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes.Darlegung_Block
					.path,
			)]: [{ Zelle_1: '' }],
		};
		const positionData = {
			attributes: {
				[getFieldNamefromDataPath(KP_P.Allgemein_Standort_Group_ID.path)]: uniqueId,
				Foerderantrag: get(application, 'id'),
				Allgemein_Kostentraeger: KOSTENTRAEGER.Antragsteller,
				Unternehmen: get(appCopy, P.Antragstellende_Organisation.path, {}),
				Allgemein_Kostenposition: KOSTEN_POSITION.Ausgabe,
				Allgemein_Kostenart_Finanzierung: KOSTEN_ARTEN.Investitionskosten,
				Allgemein_Standort_Group_ID: uniqueId,
				Allgemein_Kosten_Pro_Jahr: Array.from({ length: get(appCopy, P.Allgemein_Projektdauerin_Jahren.path, 0) }).map((_, i) => {
					return {
						Zelle_1: parseInt(get(appCopy, P.Allgemein_Projektstart_Jahr.path)) + i,
					};
				}),
			},
		};

		set(appCopy, P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path, [
			...get(appCopy, P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path, []),
			standorteData,
		]);
		set(
			appCopy,
			addIndexesToPath(P.Foerderantrag_Kostenpositions.path, [`${lastIndexInvestKosten}`], ['Foerderantrag_Kostenpositions']),
			positionData,
		);
		saveApplication(false, appCopy);
	};
	const handleDeleteStandorte = async () => {
		let appCopy = cloneDeep(application);
		const allStandorte = get(
			appCopy,
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
			[],
		);
		const allKostenposition = get(application, P.Foerderantrag_Kostenpositions.path, []);

		// Filtering out all Kostenpositions to delte
		const kostenpositionToDelete = allKostenposition.filter(
			(ele: any) => get(ele, KP_P.Allgemein_Standort_Group_ID.path) === standortGroupIdToDelete,
		);

		set(
			appCopy,
			P.Foerderantrag_Kostenpositions.path,
			allKostenposition.filter((item: any) => get(item, KP_P.Allgemein_Standort_Group_ID.path) !== standortGroupIdToDelete),
		);
		set(
			appCopy,
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
			allStandorte.filter((ele: any) => get(ele, 'Group_45_ID') !== standortGroupIdToDelete),
		);

		kostenpositionToDelete.forEach(async (ele: any) => {
			await DeleteRecord(APIS.Kostenposition, get(ele, 'id'));
		});

		saveApplication(false, appCopy);

		setStandortGroupIdToDelete('');
	};

	const anzahlStandorte = get(
		application,
		P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
		[],
	).length;

	const maxStandortErreicht: boolean = useMemo(() => {
		const foerderprogramm = get(application, P.Foerderprogramm.path, {});
		return get(foerderprogramm, FP_P.Max_46_Anzahl_Standorte.path, 100) === anzahlStandorte;
	}, [anzahlStandorte, application]);

	return (
		<>
			{get(application, P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path, []).map(
				(ele: any, i: number) => {
					if (groupId === get(ele, 'Group_45_ID', '')) {
						return (
							<Stack spacing={3} key={ele + i}>
								<Stack direction={'row'} justifyContent={'space-between'} alignItems={'baseline'}>
									<MuiDialog
										open={standortGroupIdToDelete !== ''}
										title='Standort löschen?'
										content={
											<>
												<Typography>
													<strong>Bitte beachten Sie</strong>, dass dabei auch die zugehörigen Investitionskosten gelöscht werden.
												</Typography>
												<Typography> Möchten Sie trotzdem fortfahren?</Typography>
												<Typography sx={{ fontWeight: 600 }}> Diese Aktion kann nicht rückgängig gemacht werden.</Typography>
											</>
										}
										onClose={() => setStandortGroupIdToDelete('')}
										onConfirm={() => handleDeleteStandorte()}
									/>

									<ApplicationHeading
										title={`${get(ele, 'Strasse_Und_Hausnummer') ? `Standort ${i + 1} (${get(ele, 'Strasse_Und_Hausnummer', '')})` : `Standort`} `}
										description={'Spezifikation der zu beschaffenden Ladeinfrastruktur'}
									/>
									<Stack direction={'row'} spacing={2}>
										{anzahlStandorte === i + 1 && (
											<MUIToolTip
												showTootip={maxStandortErreicht}
												title={
													'Sie können keinen weiteren Standort hinzufügen, da die maximale Anzahl an erlaubten Standorten bereits erreicht wurde.'
												}
											>
												<Button
													endIcon={<AddIcon />}
													onClick={() => handleAddStandorte()}
													disabled={maxStandortErreicht || updatedReadOnly}
												>
													Standort hinzufügen
												</Button>
											</MUIToolTip>
										)}
										{anzahlStandorte > 1 && (
											<Button
												variant={'outlined'}
												endIcon={<DeleteIcon />}
												onClick={() => setStandortGroupIdToDelete(get(ele, 'Group_45_ID', ''))}
												disabled={updatedReadOnly}
											>
												Standort Löschen
											</Button>
										)}
									</Stack>
								</Stack>

								<PruefAssistenz
									statusPath={addIndexesToPath(
										P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
											.Projektbeschreibung_Elek_0211814995_truktur_Pruefung_Pruefstatus.path,
										[`${i}`],
										['Standort_Spezifikation_Ladeinfrastruktur'],
									)}
									hinweisPath={addIndexesToPath(
										P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
											.Projektbeschreibung_Elek_0248403234_nweis_Fuer_Den_Antragsteller.path,
										[`${i}`],
										['Standort_Spezifikation_Ladeinfrastruktur'],
									)}
									ueberarbeitetAmPath={addIndexesToPath(
										P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
											.Projektbeschreibung_Elek_0182666845_ur_Pruefung_Ueberarbeitet_Am.path,
										[`${i}`],
										['Standort_Spezifikation_Ladeinfrastruktur'],
									)}
									erledigtPath={addIndexesToPath(
										P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
											.Projektbeschreibung_Elek_0176797940_rastruktur_Pruefung_Erledigt.path,
										[`${i}`],
										['Standort_Spezifikation_Ladeinfrastruktur'],
									)}
									formStruc={mapFormStruc(formStrucPruefAssistenz(), i)}
								/>

								<CreateForm
									formStruc={mapFormStruc(formStruc(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
								/>

								<Divider />
								<CreateForm
									formStruc={mapFormStruc(formStrucZielGruppe(application, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
								/>

								<Divider />
								<CreateForm
									formStruc={mapFormStruc(formStrucAbrechnung(application, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
								/>
								<Divider />

								<Box sx={{ mt: '1rem' }}>
									<Typography variant='h4'>Bitte gehen Sie hier konkret auf diesen Standort ein.</Typography>
								</Box>

								<CreateForm
									formStruc={mapFormStruc(formStrucFreiTexte(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnly}
								/>
							</Stack>
						);
					}
					return null;
				},
			)}
		</>
	);
};

export default StandorteEmobLis;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
						.Projektbeschreibung_Elek_0176797940_rastruktur_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};

const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Strasse_Und_Hausnummer,
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes.PLZ,
					validation: [{ validateFunction: Required }, { validateFunction: PostalCodeValidation }],
				},
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes.Ort,
			],
			userProps: { columnToDivide: 3 },
		},
		{
			fields: [
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
						.Darlegung_Block,
					type: FORM_FIELDS_TYPES.componentTable,
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucZielGruppe = (application: any, i: number) => {
	const zielGruppe = get(
		application,
		addIndexesToPath(
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
				.Zielgruppe_Der_Ladeinfrastruktur.path,
			[`${i}`],
			['Standort_Spezifikation_Ladeinfrastruktur'],
		),
		[],
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Zielgruppe_Der_Ladeinfrastruktur,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		...(zielGruppe.includes('Sonstige') ?
			[
				{
					fields: [
						P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
							.Zielgruppe_40_Sonstige_41,
					],
					userProps: { columnToDivide: 2 },
				},
			]
		:	[]),
	];
	return struc;
};
const formStrucAbrechnung = (application: any, i: number) => {
	const abrechnungMoeglichkeit = get(
		application,
		addIndexesToPath(
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
				.Abrechnungsmoeglichkeit_Der_Ladevorgaenge.path,
			[`${i}`],
			['Standort_Spezifikation_Ladeinfrastruktur'],
		),
		[],
	);
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Abrechnungsmoeglichkeit_Der_Ladevorgaenge,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		...(abrechnungMoeglichkeit.includes('Sonstige') ?
			[
				{
					fields: [
						P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
							.Abrechnungsmoeglichkeit_40_Sonstige_41,
					],
					userProps: { columnToDivide: 2 },
				},
			]
		:	[]),
	];
	return struc;
};
const formStrucFreiTexte = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Erlaeuterung_Des_Aufstel_0074932897_4_ufsmoeglichkeiten_Etc_4641,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Zugaenglichkeit_Und_Anfahrmoeglichkeit_Der_Ladeinfrastruktur,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Prognose_Der_Nutzungsfrequenz_47_Auslastung,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Massnahmen_Fuer_Die_Elek_0158346563_tion_Sowie_Den_Netzanschluss,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
					.Erlaeuterung_Zu_Notwendigen_Baumassnahmen,
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
						.Darlegung_Der_Notwendigk_0141341603_fikation_Des_Pufferspeichers,
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.schema.attributes
						.Darstellung_Der_Aufstellorte,
					tooltip: 'Bitte hier als Karte uploaden',
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const mapFormStruc = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path: addIndexesToPath(pathWithoutIndex, [`${index}`], ['Standort_Spezifikation_Ladeinfrastruktur']),
				};
			}),
		};
	});

	return result;
};

export const getFormStrucStandorteEmobLis = (application: any, groupId: string) => {
	const alleStandorte = get(
		application,
		P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Standort_Spezifikation_Ladeinfrastruktur.path,
		[],
	);
	let completeStruc: FormStrucItem[] = [];

	alleStandorte.forEach((standort: any, index: number) => {
		if (groupId === get(standort, 'Group_45_ID', '')) {
			const formStrucPruefAssistezWithIndex = mapFormStruc(formStrucPruefAssistenz(), index);
			const formStrucWithIndex = mapFormStruc(formStruc(), index);
			const formStrucZielGruppeWithIndex = mapFormStruc(formStrucZielGruppe(application, index), index);
			const formStrucAbrechnungWithIndex = mapFormStruc(formStrucAbrechnung(application, index), index);
			const formStrucFreiTexteWithIndex = mapFormStruc(formStrucFreiTexte(), index);

			if (
				checkIfPruefAssitenzNeedsToValidate(
					standort,
					'Projektbeschreibung_Elek_0211814995_truktur_Pruefung_Pruefstatus',
					'Projektbeschreibung_Elek_0176797940_rastruktur_Pruefung_Erledigt',
				)
			) {
				completeStruc = concat(
					formStrucPruefAssistezWithIndex,
					formStrucWithIndex,
					formStrucZielGruppeWithIndex,
					formStrucAbrechnungWithIndex,
					formStrucFreiTexteWithIndex,
				);
			} else {
				completeStruc = concat(formStrucWithIndex, formStrucZielGruppeWithIndex, formStrucAbrechnungWithIndex, formStrucFreiTexteWithIndex);
			}
		}
	});

	return completeStruc;
};
